/** @typedef {import("../../api/types").Stats} Stats */
/** @typedef {import("../../api/types").MonthlyStats} MonthlyStats */
/** @typedef {import("../../api/types").Pipeline} Pipeline */
/** @typedef {import("../../api/types").StatsMix} StatsMix */
import React, {useEffect, useState} from "react";
import axios from "axios";
import {Col, Container, Row} from "reactstrap";
import InfoTip from "../../shared/components/InfoTip";
import {formatMoney, formatPercent} from "../../shared/utils/format";
import MonthChart from "./components/MonthChart";
import PipelineCard from "./components/PipelineCard";
import MixCard from "./components/MixCard";
import {marginOf, periodFigures, ticketOf} from "./utils/monthSeries";
import "./accounting.css";

const API = "/os/api/";

// The cards, in order. `money` ones are amounts in the provider's currency and
// `percent` ones a percentage; a figure that can't be worked out (no paid order
// yet) is null and shown as a dash. `hint` says what the figure covers (shown
// in a tooltip).
/** @type {{key: keyof Omit<Stats, "currency">, label: string, hint: string, money?: boolean, percent?: boolean}[]} */
const STATS = [
    {
        key: "orders_last_30_days",
        label: "Ordens nos últimos 30 dias",
        hint: "Ordens criadas nos últimos 30 dias, contando a partir de agora. Ordens canceladas ou expiradas não entram.",
    },
    {
        key: "orders_this_month",
        label: "Ordens neste mês",
        hint: "Ordens criadas desde o dia 1º deste mês. Ordens canceladas ou expiradas não entram.",
    },
    {
        key: "revenue_last_30_days",
        label: "Receita nos últimos 30 dias",
        hint: "Soma das ordens pagas nos últimos 30 dias, pela data em que foram marcadas como pagas. Ordens ainda não pagas não entram.",
        money: true,
    },
    {
        key: "revenue_this_month",
        label: "Receita neste mês",
        hint: "Soma das ordens pagas desde o dia 1º deste mês, pela data em que foram marcadas como pagas. Ordens ainda não pagas não entram.",
        money: true,
    },
    {
        key: "profit_last_30_days",
        label: "Lucro nos últimos 30 dias",
        hint: "Receita menos o custo dos serviços e produtos das ordens pagas nos últimos 30 dias. Não desconta despesas fixas, como aluguel ou energia.",
        money: true,
    },
    {
        key: "profit_this_month",
        label: "Lucro neste mês",
        hint: "Receita menos o custo dos serviços e produtos das ordens pagas desde o dia 1º. Não desconta despesas fixas, como aluguel ou energia.",
        money: true,
    },
    {
        key: "margin_this_month",
        label: "Margem neste mês",
        hint: "O lucro como percentual da receita, nas ordens pagas desde o dia 1º. Fica em branco enquanto não há ordem paga.",
        percent: true,
    },
    {
        key: "average_ticket_this_month",
        label: "Ticket médio neste mês",
        hint: "A receita dividida pelo número de ordens pagas desde o dia 1º. Fica em branco enquanto não há ordem paga.",
        money: true,
    },
];

// What a card shows: the figure in its unit, or a dash while it is not known.
/** @param {Stats|null} stats @param {{key: keyof Omit<Stats, "currency">, money?: boolean, percent?: boolean}} card */
const figure = (stats, {key, money, percent}) => {
    if (!stats) return "–";
    const value = stats[key];
    if (value === null || value === undefined) return "–";
    if (money) return formatMoney(value, stats.currency);
    if (percent) return formatPercent(Number(value));
    return value;
};

/**
 * The numbers of the business: what came in, what it cost, what is left, what
 * is on its way, month by month. The front page is for what needs doing;
 * this one is for looking at the figures.
 */
const Accounting = () => {
    const [stats, setStats] = useState(/** @type {Stats|null} */ (null));
    const [monthly, setMonthly] = useState(/** @type {MonthlyStats|null} */ (null));
    const [monthlyFailed, setMonthlyFailed] = useState(false);
    const [pipeline, setPipeline] = useState(/** @type {Pipeline|null} */ (null));
    const [pipelineFailed, setPipelineFailed] = useState(false);
    const [mix, setMix] = useState(/** @type {StatsMix|null} */ (null));
    const [mixFailed, setMixFailed] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        const {signal} = controller;
        // Each part loads (and fails) on its own.
        axios
            .get(`${API}stats/`, {signal})
            .then(({data}) => setStats(data))
            .catch(() => {});
        axios
            .get(`${API}stats/monthly/`, {signal})
            .then(({data}) => setMonthly(data))
            .catch((err) => {
                if (!axios.isCancel(err)) setMonthlyFailed(true);
            });
        axios
            .get(`${API}stats/pipeline/`, {signal})
            .then(({data}) => setPipeline(data))
            .catch((err) => {
                if (!axios.isCancel(err)) setPipelineFailed(true);
            });
        axios
            .get(`${API}stats/mix/`, {signal})
            .then(({data}) => setMix(data))
            .catch((err) => {
                if (!axios.isCancel(err)) setMixFailed(true);
            });
        return () => controller.abort();
    }, []);

    const months = monthly?.months ?? null;
    const currency = monthly?.currency ?? "";
    const money = (/** @type {string|number} */ value) => formatMoney(value, currency);
    const period = periodFigures(months ?? []);
    const expected = monthly && Number(monthly.expected.revenue) > 0 ? monthly.expected : null;
    // what failed or is still loading, the same for every chart of the month series
    const common = {months, error: monthlyFailed, currency};

    return (
        <Container className="mt-4 mb-5">
            <h3 className="mb-1">Contabilidade</h3>
            <p className="text-muted mb-3">
                Os números do negócio: o que entrou, o que custou, o que sobrou e o que está a caminho.
            </p>

            <Row className="g-3 mb-4">
                {STATS.map((card) => (
                    <Col xs="6" lg="3" key={card.key}>
                        <div className="accounting-stat">
                            <div className="accounting-stat-value">{figure(stats, card)}</div>
                            <div className="accounting-stat-label d-flex align-items-center gap-1">
                                {card.label}
                                <InfoTip>{card.hint}</InfoTip>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>

            <div className="mb-4">
                <PipelineCard data={pipeline} error={pipelineFailed} />
            </div>

            <div className="mb-4">
                <MonthChart
                    trend
                    {...common}
                    title="Receita e lucro por mês"
                    subtitle="Ordens pagas, no mês do pagamento."
                    ariaLabel="Receita e lucro por mês"
                    emptyMessage="Ainda não há ordens pagas nos últimos 12 meses."
                    unit="money"
                    series={[
                        {key: "revenue", label: "Receita", tone: "revenue", value: (m) => Number(m.revenue)},
                        {key: "profit", label: "Lucro", tone: "profit", value: (m) => Number(m.profit)},
                    ]}
                    headline={
                        monthly && (
                            <>
                                Total: <strong>{money(monthly.totals.revenue)}</strong>
                                {" · lucro "}
                                <span className="text-nowrap">
                                    <strong>{money(monthly.totals.profit)}</strong>
                                    <InfoTip>A soma dos 12 meses, incluindo o mês em andamento.</InfoTip>
                                </span>
                            </>
                        )
                    }
                    columns={[
                        {header: "Receita", cell: (m) => money(m.revenue), total: money(monthly?.totals.revenue ?? 0)},
                        {header: "Custo", cell: (m) => money(m.cost), total: money(monthly?.totals.cost ?? 0)},
                        {header: "Lucro", cell: (m) => money(m.profit), total: money(monthly?.totals.profit ?? 0)},
                    ]}
                    expectedNote={
                        expected && (
                            <>
                                Esperado: <strong>+{money(expected.revenue)}</strong> de receita e{" "}
                                <strong>+{money(expected.profit)}</strong> de lucro (ordens a receber e confirmadas)
                            </>
                        )
                    }
                />
            </div>

            <Row className="g-4 mb-4">
                <Col xs="12" lg="6">
                    <MonthChart
                        trend
                        {...common}
                        title="Ordens por mês"
                        subtitle="Ordens criadas em cada mês, sem as canceladas e expiradas."
                        ariaLabel="Ordens criadas por mês"
                        emptyMessage="Ainda não há ordens nos últimos 12 meses."
                        kind="bars"
                        unit="whole"
                        series={[{key: "orders", label: "Ordens", tone: "orders", value: (m) => m.orders}]}
                        headline={
                            monthly && (
                                <>
                                    Total: <strong>{monthly.totals.orders}</strong>
                                </>
                            )
                        }
                        columns={[
                            {
                                header: "Ordens",
                                cell: (m) => String(m.orders),
                                total: String(monthly?.totals.orders ?? 0),
                            },
                        ]}
                    />
                </Col>
                <Col xs="12" lg="6">
                    <MonthChart
                        trend
                        {...common}
                        title="Ticket médio por mês"
                        subtitle="A receita dividida pelas ordens pagas no mês."
                        ariaLabel="Ticket médio por mês"
                        emptyMessage="Ainda não há ordens pagas nos últimos 12 meses."
                        unit="money"
                        series={[{key: "ticket", label: "Ticket médio", tone: "ticket", value: ticketOf}]}
                        headline={
                            period.ticket !== null && (
                                <>
                                    Nos 12 meses: <strong>{money(period.ticket)}</strong>
                                </>
                            )
                        }
                        columns={[
                            {header: "Ordens pagas", cell: (m) => String(m.paid_orders)},
                            {
                                header: "Ticket médio",
                                cell: (m) => (ticketOf(m) === null ? "–" : money(ticketOf(m) ?? 0)),
                            },
                        ]}
                    />
                </Col>
            </Row>

            <Row className="g-4 mb-4">
                <Col xs="12" lg="6">
                    <MonthChart
                        trend
                        {...common}
                        title="Margem por mês"
                        subtitle="O lucro como percentual da receita."
                        ariaLabel="Margem por mês"
                        emptyMessage="Ainda não há ordens pagas nos últimos 12 meses."
                        unit="percent"
                        series={[{key: "margin", label: "Margem", tone: "margin", value: marginOf}]}
                        headline={
                            period.margin !== null && (
                                <>
                                    Nos 12 meses: <strong>{formatPercent(period.margin)}</strong>
                                </>
                            )
                        }
                        columns={[
                            {header: "Lucro", cell: (m) => money(m.profit)},
                            {
                                header: "Margem",
                                cell: (m) => (marginOf(m) === null ? "–" : formatPercent(marginOf(m) ?? 0)),
                            },
                        ]}
                    />
                </Col>
                <Col xs="12" lg="6">
                    <MonthChart
                        trend
                        {...common}
                        title="Serviços e produtos por mês"
                        subtitle="A receita de cada mês, dividida entre serviços e produtos."
                        ariaLabel="Receita de serviços e de produtos por mês"
                        emptyMessage="Ainda não há ordens pagas nos últimos 12 meses."
                        kind="bars"
                        unit="money"
                        series={[
                            {
                                key: "services",
                                label: "Serviços",
                                tone: "services",
                                value: (m) => Number(m.services_revenue),
                            },
                            {
                                key: "products",
                                label: "Produtos",
                                tone: "products",
                                value: (m) => Number(m.products_revenue),
                            },
                        ]}
                        columns={[
                            {header: "Serviços", cell: (m) => money(m.services_revenue)},
                            {header: "Produtos", cell: (m) => money(m.products_revenue)},
                        ]}
                    />
                </Col>
            </Row>

            <MixCard data={mix} error={mixFailed} />
        </Container>
    );
};

export default Accounting;
